import {
  Sparkles, BookOpen, LayoutDashboard, Mic, Users, LayoutList, Target, GitMerge, Map,
  Lightbulb, Briefcase, LayoutGrid, GitBranch, FolderTree, ClipboardList, Network, FileEdit,
  MessageSquare, FlaskConical, Globe, BarChart3, BadgeCheck, Compass, Bot, FileUp,
  type LucideIcon,
} from 'lucide-react'

export type PhaseStatus = 'active' | 'completed' | 'available' | 'locked'

export interface Phase {
  id: string
  number: number
  label: string
  defaultView: string
  viewIds: string[]
  progress: number
}

export interface Tool {
  id: string
  label: string
  icon: LucideIcon
  view: string
}

export interface NavItem {
  id: string
  label: string
  icon: LucideIcon
}

export interface NavGroup {
  id: string
  label?: string
  subtext?: string
  domain?: 'market' | 'solution' | 'business'
  collapsible?: boolean
  items: NavItem[]
}

export interface AdvancedGroup {
  id: string
  label: string
  items: NavItem[]
}

export const DOMAIN_COLORS: Record<string, string> = {
  market: 'var(--color-market-dark)',
  solution: 'var(--color-solution-dark)',
  business: 'var(--color-business-dark)',
}

export const PHASES: Phase[] = [
  {
    id: 'explore',
    number: 1,
    label: 'Explore',
    defaultView: 'ai-interview',
    viewIds: ['ai-interview', 'interviewer', 'sources', 'discovery', 'persona-workshop', 'persona-configurator', 'competitor-map'],
    progress: 0,
  },
  {
    id: 'define',
    number: 2,
    label: 'Define',
    defaultView: 'value-design',
    viewIds: ['value-design', 'analysis', 'dedup', 'fit-thesis', 'canvas-solution'],
    progress: 0,
  },
  {
    id: 'build-solution',
    number: 3,
    label: 'Build Solution',
    defaultView: 'requirements',
    viewIds: ['requirements', 'smartdocs', 'solution-configurator', 'dual-space', 'docs'],
    progress: 0,
  },
  {
    id: 'market-strategy',
    number: 4,
    label: 'Market Strategy',
    defaultView: 'canvas-market',
    viewIds: ['canvas-market', 'canvas-business', 'canvas'],
    progress: 0,
  },
  {
    id: 'iterate',
    number: 5,
    label: 'Iterate',
    defaultView: 'reports',
    viewIds: ['reports'],
    progress: 0,
  },
]

export const TOOLS: Tool[] = [
  { id: 'coach', label: 'AI Coach', icon: Sparkles, view: 'discovery-assistant' },
  { id: 'knowledge', label: 'Knowledge Base', icon: BookOpen, view: 'knowledge' },
]

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    items: [{ id: 'workspace', label: 'Workspace', icon: LayoutDashboard }],
  },
  {
    id: 'market',
    label: 'Market',
    subtext: 'Who has the problem?',
    domain: 'market',
    items: [
      { id: 'ai-interview', label: 'Interviews', icon: Mic },
      { id: 'persona-workshop', label: 'Personas', icon: Users },
      { id: 'market-segmentation', label: 'Segments', icon: LayoutList },
      { id: 'competitor-map', label: 'Competitors', icon: Target },
      { id: 'canvas-market', label: 'Positioning', icon: Map },
    ],
  },
  {
    id: 'solution',
    label: 'Solution',
    subtext: 'What do we build?',
    domain: 'solution',
    items: [
      { id: 'value-design', label: 'Value Design', icon: GitMerge },
      { id: 'fit-thesis', label: 'Fit Thesis', icon: Network },
      { id: 'canvas-solution', label: 'Problem Statement', icon: Lightbulb },
      { id: 'requirements', label: 'Requirements', icon: ClipboardList },
    ],
  },
  {
    id: 'business',
    label: 'Business',
    subtext: 'How do we make money?',
    domain: 'business',
    items: [
      { id: 'canvas-business', label: 'Business Model', icon: Briefcase },
      { id: 'analysis', label: 'Validation', icon: BadgeCheck },
    ],
  },
  {
    id: 'sources',
    label: 'Sources',
    collapsible: true,
    items: [
      { id: 'interviewer', label: 'Interviews', icon: MessageSquare },
      { id: 'docs', label: 'Docs', icon: FlaskConical },
      { id: 'web', label: 'Web', icon: Globe },
    ],
  },
]

export const ADVANCED_GROUPS: AdvancedGroup[] = [
  {
    id: 'deliverables',
    label: 'Deliverables',
    items: [
      { id: 'canvas', label: 'Canvas', icon: LayoutGrid },
      { id: 'smartdocs', label: 'Smart Docs', icon: FileEdit },
    ],
  },
  {
    id: 'solution-tools',
    label: 'Solution Tools',
    items: [
      { id: 'dual-space', label: 'Problem-Solution', icon: GitBranch },
      { id: 'solution-configurator', label: 'Configurator', icon: FolderTree },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    items: [
      { id: 'dedup', label: 'Dedup', icon: GitMerge },
      { id: 'ontology', label: 'Ontology', icon: Network },
      { id: 'graph', label: 'Graph', icon: GitBranch },
      { id: 'tree', label: 'Tree View', icon: FolderTree },
      { id: 'knowledge', label: 'Knowledge Base', icon: BookOpen },
    ],
  },
  {
    id: 'legacy',
    label: 'Legacy',
    items: [
      { id: 'discovery', label: 'Discovery', icon: Compass },
      { id: 'discovery-assistant', label: 'AI Coach', icon: Bot },
      { id: 'pdf', label: 'Documents', icon: FileUp },
      { id: 'crm', label: 'Customers', icon: Users },
      { id: 'reports', label: 'Reports', icon: BarChart3 },
    ],
  },
]

export const ALL_ADVANCED_IDS = ADVANCED_GROUPS.flatMap(g => g.items.map(i => i.id))

export const ADVANCED_ITEMS: { id: string; label: string }[] = [
  { id: 'workspace', label: 'Workspace Dashboard' },
  { id: 'ontology', label: 'Ontology' },
  { id: 'graph', label: 'Graph' },
  { id: 'tree', label: 'Tree View' },
  { id: 'web', label: 'Web Sources' },
  { id: 'pdf', label: 'PDF' },
  { id: 'crm', label: 'Customers' },
]

export function getPhaseStatus(phase: Phase, activePhaseId: string | null): PhaseStatus {
  if (phase.id === activePhaseId) return 'active'
  if (phase.progress >= 100) return 'completed'
  if (phase.progress > 0) return 'available'
  return 'locked'
}
